import { Grid } from '@mui/material';
import { stateMap } from '@tassat/common-definitions';
import { FirmRecord } from '@tassat/fred-definitions';
import { ActionTypes, SelectOption } from '@tassat/ui-definitions';
import { capitalizeFirstLetter } from '@tassat/utility';
import { Formik, FormikProps } from 'formik';
import * as React from 'react';

import { validateWithdrawalForm } from './submission';
import {
    IErrors,
    IFormValues,
    IFormikProps,
    withdrawalInstructionTypes
} from './types';
import { FormContents } from './WithdrawalFormContents';
import { emptySelectOption } from '../helpers/constant';

const WithdrawalForm = (props: IFormikProps) => {
    const { classes, onClose, formProps } = props;

    const {
        actionType = ActionTypes.add,
        submitWithdrawalForm,
        arePendingTransactions,
        editWithdrawalInstruction,
        firmWithdrawalInstructions,
        deleteWithdrawalInstruction,
        firm
    } = formProps;

    const [ submitError, setSubmitError ] = React.useState<string>('');

    let initialValues = {} as IFormValues;
    if (actionType === ActionTypes.add) {
        initialValues = {
            Description: '',
            WithdrawalType: emptySelectOption,
            BeneName: '',
            BeneBankAcct: '',
            BeneStreetAddress: '',
            BeneCity: '',
            BeneStateProv: '',
            BenePostalCode: '',
            BeneCountry: '',
            BeneBankName: '',
            BeneBankAccountType: emptySelectOption,
            BeneBankRoutNum: '',
            BeneBankBic: '',
            BeneBankStreetAddress: '',
            BeneBankCity: '',
            BeneBankStateProv: '',
            BeneBankPostalCode: '',
            BeneBankCountry: '',
            IntermediaryBankName: '',
            IntermediaryBankRoutNum: '',
            IntermediaryBankBic: '',
            IntermediaryBankStreetAddress: '',
            IntermediaryBankCity: '',
            IntermediaryBankStateProv: '',
            IntermediaryBankPostalCode: '',
            IntermediaryBankCountry: '',
            BeneInfo1: '',
            BeneInfo2: '',
            BeneInfo3: '',
            BeneInfo4: '',
            BankToBankInfo1: '',
            BankToBankInfo2: '',
            BankToBankInfo3: '',
            BankToBankInfo4: '',
            Blockchain: emptySelectOption,
            Address: ''
        };
    } else if (actionType === ActionTypes.edit) {
        if (!editWithdrawalInstruction) return <div />;

        let beneStateProv: SelectOption | string = editWithdrawalInstruction.BeneStateProv;
        if (editWithdrawalInstruction.BeneCountry === 'US') {
            beneStateProv = {
                label: stateMap[beneStateProv].isoCode,
                value: stateMap[beneStateProv].isoCode
            };
        }

        let beneBankStateProv: SelectOption | string = editWithdrawalInstruction.BeneBankStateProv;
        if (editWithdrawalInstruction.BeneBankCountry === 'US') {
            beneBankStateProv = {
                label: stateMap[beneBankStateProv].isoCode,
                value: stateMap[beneBankStateProv].isoCode
            };
        }

        let intBankStateProv: SelectOption | string = editWithdrawalInstruction.IntermediaryBankStateProv;
        if (editWithdrawalInstruction.IntermediaryBankCountry === 'US') {
            intBankStateProv = {
                label: stateMap[intBankStateProv].isoCode,
                value: stateMap[intBankStateProv].isoCode
            };
        }

        initialValues = {
            Description: editWithdrawalInstruction.Description,
            WithdrawalType: withdrawalInstructionTypes
                .find((typeOption) => typeOption.value.toString() === String
                    .fromCharCode(editWithdrawalInstruction.WithdrawalType as unknown as number)) ??
                    emptySelectOption,
            BeneName: editWithdrawalInstruction.BeneName,
            BeneBankAcct: editWithdrawalInstruction.BeneBankAcct,
            BeneStreetAddress: editWithdrawalInstruction.BeneStreetAddress,
            BeneCity: editWithdrawalInstruction.BeneCity,
            BeneStateProv: beneStateProv,
            BenePostalCode: editWithdrawalInstruction.BenePostalCode,
            BeneCountry: editWithdrawalInstruction.BeneCountry,
            BeneBankName: editWithdrawalInstruction.BeneBankName,
            BeneBankAccountType: {
                label: capitalizeFirstLetter(editWithdrawalInstruction.BeneBankAccountType, true),
                value: capitalizeFirstLetter(editWithdrawalInstruction.BeneBankAccountType, true)
            },
            BeneBankRoutNum: editWithdrawalInstruction.BeneBankRoutNum,
            BeneBankBic: editWithdrawalInstruction.BeneBankBic,
            BeneBankStreetAddress: editWithdrawalInstruction.BeneBankStreetAddress,
            BeneBankCity: editWithdrawalInstruction.BeneBankCity,
            BeneBankStateProv: beneBankStateProv,
            BeneBankPostalCode: editWithdrawalInstruction.BeneBankPostalCode,
            BeneBankCountry: editWithdrawalInstruction.BeneBankCountry,
            IntermediaryBankName: editWithdrawalInstruction.IntermediaryBankName,
            IntermediaryBankRoutNum: editWithdrawalInstruction.IntermediaryBankRoutNum,
            IntermediaryBankBic: editWithdrawalInstruction.IntermediaryBankBic,
            IntermediaryBankStreetAddress: editWithdrawalInstruction.IntermediaryBankStreetAddress,
            IntermediaryBankCity: editWithdrawalInstruction.IntermediaryBankCity,
            IntermediaryBankStateProv: intBankStateProv,
            IntermediaryBankPostalCode: editWithdrawalInstruction.IntermediaryBankPostalCode,
            IntermediaryBankCountry: editWithdrawalInstruction.IntermediaryBankCountry,
            BeneInfo1: editWithdrawalInstruction.BeneInfo1,
            BeneInfo2: editWithdrawalInstruction.BeneInfo2,
            BeneInfo3: editWithdrawalInstruction.BeneInfo3,
            BeneInfo4: editWithdrawalInstruction.BeneInfo4,
            BankToBankInfo1: editWithdrawalInstruction.BankToBankInfo1,
            BankToBankInfo2: editWithdrawalInstruction.BankToBankInfo2,
            BankToBankInfo3: editWithdrawalInstruction.BankToBankInfo3,
            BankToBankInfo4: editWithdrawalInstruction.BankToBankInfo4,
            Blockchain: editWithdrawalInstruction.Blockchain ?
                { label: editWithdrawalInstruction.Blockchain, value: editWithdrawalInstruction.Blockchain }
                : emptySelectOption,
            Address: editWithdrawalInstruction.Address
        };
    }

    const firmWithdrawalFormOriginalValidations = async (values: IFormValues, firm: FirmRecord) => {
        const errors: IErrors = {} as IErrors;
        await validateWithdrawalForm(values, {
            errors,
            editWithdrawalInstruction,
            firmWithdrawalInstructions,
            actionType,
            arePendingTransactions
        });

        return errors;
    };

    return (
        <Grid
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            <Grid
                item
                xs={12}
            >
                <Formik
                    initialTouched={{ Description: true }}
                    initialValues={initialValues}
                    onSubmit={async (values: IFormValues) => {
                        await submitWithdrawalForm(values, {
                            actionType,
                            editWithdrawalInstruction: editWithdrawalInstruction || null,
                            onClose,
                            setSubmitError,
                            firmId: firm.FirmId
                        });
                    }}
                    validate={(values: IFormValues) => firmWithdrawalFormOriginalValidations(values, firm)}
                    validateOnChange
                    validateOnMount
                >
                    {(withdrawalFormProps: FormikProps<IFormValues>) => (
                        <FormContents
                            {...withdrawalFormProps}
                            actionType={actionType}
                            classes={classes}
                            deleteWithdrawalInstruction={deleteWithdrawalInstruction}
                            editWithdrawalInstruction={editWithdrawalInstruction}
                            formProps={formProps}
                            onClose={onClose}
                            submitError={submitError}
                        />
                    )}
                </Formik>
            </Grid>
        </Grid>
    );
};

export default WithdrawalForm;